import React from 'react';
import DOMPurify from 'dompurify';
import config from 'config/config';
import { parseSortColumn, compareSortColumn } from 'utilities/ncdmUtils';
import muiTheme from '../theme';

// Treating any content with html as unsafe. DOMPurify used to sanitize html to reduce XSS risk.
export const createMarkup = (dirty) => {
    const clean = DOMPurify.sanitize(dirty, {
        USE_PROFILES: { html: true },
        ADD_ATTR: ['target'],
    });
    return { __html: clean };
};

export const handleFetchError = (response) => {
    if (!response.ok) {
        throw Error(
            `Fetch error status: ${response.status}; statusText: ${response.statusText}`
        );
    }
    return response;
};

export const a11yProps = (index, description) => ({
    id: `${description}-${index}`,
    'aria-controls': `${description}-${index}`,
});

// Method to return a number formatted to one decimal place
export const formatNumber = (value) => {
    if (typeof value !== 'number') {
        return value;
    }
    return (Math.round(value * 10) / 10).toFixed(1);
};

// Method to format a single map popup value for display. Returns the sentinel's label rather than
// the number when data is missing or suppressed, and an empty string when there is no value at all.
// Used by the popup content types in components/Map/popupContentTypes.js.
export const formatPopupValue = (value, unit = '', field = '') => {
    if (value === 0) {
        return '0.0';
    }
    if (!value) {
        return '';
    }
    if (value === config.missing) {
        return 'Missing';
    }
    if (value === config.suppressed) {
        return 'Suppressed';
    }
    // Add .0 to whole numbers if it's a percentage rate
    if (unit === '%' || (field && field.toLowerCase().includes('rate'))) {
        return `${formatNumber(value)}${unit}`;
    }
    return `${value.toLocaleString()}${unit}`;
};

// The width, in pixels, of the invisible line that holds the data discontinuity tooltip.
const HOVER_BAND_WIDTH = 100;

// The chart width, in pixels, at or below which the discontinuity tooltip wraps onto two lines. This
// matches the 'MAX_MOBILE_VIEW_WIDTH' the chart components use, but is measured against the chart
// rather than the window so the tooltip re-wraps on a resize without the component re-rendering.
const MAX_MOBILE_CHART_WIDTH = 500;

// Method to create the data discontinuity annotation for the chart. This is used to indicate that the data
// changed at some point in time. The annotation is a line and box that appears on the chart to indicate the change.
// Where the graphic is drawn comes from the tab's chartSettings (see the README):
//   discontinuityIndexValue - the x-axis label the graphic is drawn after
//   discontinuityEndValue - the x-axis label the graphic ends on, defaults to the label after the index value
//   discontinuityChangeLabel - the wording named in the tooltip, defaults to the index value
//   discontinuityRequiresStratification - only show the graphic on stratified displays
// The x-axis is not always a year - some charts plot quarters such as '2024 Jul-Sep' - so these values are
// matched against the chart labels as strings.
// Stratification is an optional argument:
export const generateAnnotations = (
    data,
    chartSettings,
    stratification = null
) => {
    const {
        discontinuityIndexValue,
        discontinuityEndValue,
        discontinuityChangeLabel,
        discontinuityRequiresStratification,
    } = chartSettings ?? {};

    // Some displays, such as Birth Outcomes, only changed the data behind the stratified
    // categories, so the graphic is hidden on the non-stratified display.
    if (discontinuityRequiresStratification && !stratification) {
        return {};
    }

    // Labels can be numbers in the chart data but strings in the config, so compare them as strings.
    const labels = data?.labels ?? [];
    const findLabelIndex = (value) =>
        labels.findIndex((label) => String(label) === String(value));

    const startIndex = findLabelIndex(discontinuityIndexValue);
    // Without a configured end value the graphic covers a single interval, ending at the next label.
    const endIndex = discontinuityEndValue
        ? findLabelIndex(discontinuityEndValue)
        : startIndex + 1;

    // Do not add the annotation unless both ends of the range are found in the chart's data and the
    // range moves forward without running off the end of the chart. A misconfigured value draws
    // nothing rather than a plausible looking box of the wrong width. This also covers a display
    // flagged for the graphic before its values have been configured.
    if (
        startIndex === -1 ||
        endIndex <= startIndex ||
        endIndex > labels.length - 1
    ) {
        return {};
    }

    // The wording of the change can differ from the value used to position the graphic above.
    const changeLabelForTooltip =
        discontinuityChangeLabel ?? discontinuityIndexValue;

    // The plugin draws one line of the tooltip per entry in the 'content' array. The change label is
    // configured per display and can be as long as '2024 Jul-Sep to 2025 Oct-Dec', so on a narrow
    // chart the sentences are given a line each rather than running past the plot area.
    const tooltipLines = [
        `Data changed in ${changeLabelForTooltip}.`,
        'See About for details.',
    ];

    // The details for this method can be found in the older version of the chartjs-plugin-annotation
    // documentation: https://www.chartjs.org/chartjs-plugin-annotation/1.2.0/samples/line/labelVisibility.html
    // Both the line and the box below listen for hover, because the plugin hands the hover to only one
    // annotation at a time and the two sit on top of each other - which of them is considered hovered
    // changes as the pointer moves across the graphic, and every one of those hand offs fires a 'leave'
    // followed by an 'enter'. Redrawing inside the handlers therefore made the tooltip flicker, and
    // because Chart.js replays the last mouse event at the end of every 'chart.update()', each redraw
    // fired the handlers again until the call stack was exhausted. So the handlers only record whether
    // the tooltip should be showing, and the redraw is left to the next animation frame, by which point
    // a hand off has settled. The redraw is skipped when the chart is already drawn that way, which is
    // what stops the replayed event from starting the cycle over again.
    let drawnTooltipEnabled = false;
    let redrawRequested = false;

    const redrawTooltip = (chart, labelOptions) => {
        redrawRequested = false;

        // The chart is gone if the display was closed within the frame.
        if (!chart.ctx || labelOptions.enabled === drawnTooltipEnabled) {
            return;
        }

        drawnTooltipEnabled = labelOptions.enabled;
        chart.update();
    };

    const setTooltipVisibility = (ctx, e, enabled) => {
        const { chart } = ctx;

        const labelOptions =
            chart.config.options.plugins.annotation.annotations.line.label;
        labelOptions.enabled = enabled;

        // Setting the popup location to the y value of the mouse over event. Only the event that
        // brings the tooltip up moves it, so it stays put through the hand offs described above.
        if (enabled && !drawnTooltipEnabled) {
            labelOptions.position = `${(e.y / chart.chartArea.height) * 100}%`;
        }

        if (!redrawRequested) {
            redrawRequested = true;
            requestAnimationFrame(() => redrawTooltip(chart, labelOptions));
        }
    };

    const hoverHandlers = {
        enter(ctx, e) {
            setTooltipVisibility(ctx, e, true);
        },
        leave(ctx, e) {
            setTooltipVisibility(ctx, e, false);
        },
    };

    return {
        line: {
            // This is a non-visible line underneath the 'box' below. It holds the tooltip, which is drawn
            // along the line, so the line is centered in the box.
            type: 'line',
            mode: 'vertical',
            borderColor:
                muiTheme.chartPalette.discontinuity.tooltipTriggerBorder,
            // The band is wider than a single interval so the tooltip is easy to hit on a narrow box.
            borderWidth: HOVER_BAND_WIDTH,
            // For a single interval this is the '.5' between the two sections, as it has always been.
            xMin: (startIndex + endIndex) / 2,
            xMax: (startIndex + endIndex) / 2,
            label: {
                // Scriptable so the wrap follows the width of the chart it is drawn on. The plugin
                // resolves this when the chart updates, which includes a resize, so the tooltip
                // re-wraps without the chart component having to re-render.
                content: ({ chart }) =>
                    chart.width <= MAX_MOBILE_CHART_WIDTH
                        ? tooltipLines
                        : [tooltipLines.join('  ')],
                enabled: false,
                textAlign: 'center',
            },
            ...hoverHandlers,
        },
        box: {
            type: 'box',
            backgroundColor: muiTheme.chartPalette.discontinuity.boxBackground,
            borderDash: [6, 6],
            borderWidth: 2,
            xMin: startIndex,
            xMax: endIndex,
            drawTime: 'afterDatasetsDraw',
            // The box listens as well so that the whole shaded area shows the tooltip, however wide the range is.
            ...hoverHandlers,
        },
    };
};

/**
 * This object is for generating custom styling objects for use within the 'LegendItems.js' file. We could store the
 * legend style information in the database, but for some cases the style information is included in the FeatureLayer and
 * this object is used to set the legend style and enables us to only store and maintain the style information in one place.
 * Layers that require custom styling are flagged with the parameter 'customStyleTitle'.
 * For example, a value of this parameter could be:  'customLegendStyleNames.eseriSMSCircleFilled.title'.
 *
 * This is to make the subsequent 'styleMethod' accesible from the 'LegendItems.js' file where the
 * 'customStyleTitle' of a layer is used to reference the correct object.  Example:
 *
 * customLegendStyleNames[layer.customStyleTitle].styleMethod()
 *
 * The idea is to have the 'customLegendStyleNames' object grow as we encounter more legend items that
 * require unique styling.  As the object does expand, we want to make sure that we are following esri symbol
 * naming conventions to some degree, attaching a descriptor on the end that explains what kind of style it is we are returning.
 *
 * Example: esriSMSTriangleShaded : {}
 *
 */
export const customLegendStyleNames = {
    esriSMSCircleFilled: {
        title: 'esriSMSCircleFilled',
        styleMethod: function styleMethod(layerStyle, layerOn) {
            const [r, g, b] = layerStyle.color;
            return {
                height: `${layerStyle.size * 2}px`,
                width: `${layerStyle.size * 2}px`,
                borderColor: layerOn
                    ? 'transparent'
                    : muiTheme.palette.grey[300],
                backgroundColor: layerOn
                    ? `rgba(${r},${g},${b}, 1)`
                    : 'transparent',
            };
        },
    },
};

const handleConfidenceIntervalRowConstruction = (features, subSets, strat) => {
    const allRows = [];
    let index = 0;
    const workingFeatures = features[strat];
    subSets.forEach((subSet) => {
        const nestedData = Object.entries(workingFeatures[subSet]);
        nestedData.forEach(([label, data]) => {
            const workingRow = {
                id: `${label}-${index}`,
                year: features.year, // same year for all rows,
                type: data.type,
                condition: data.condition,
                rate: data.x,
                lowerCI: data.xMin,
                upperCI: data.xMax,
            };
            allRows.push(workingRow);
            index += 1;
        });
    });

    return allRows;
};

export const formatRows = (layer, stratification, currentTab) => {
    const confidenceIntervalBar =
        currentTab.chartSettings?.chartType ===
        'confidenceIntervalHorizontalBar';

    let index = 0;
    const dataArray = [];
    const sortField = layer.customSortField || 'County';
    let dataSubSets = [];

    if (confidenceIntervalBar && layer.features[0][stratification]) {
        // Injury from fall BRFSS chart uses a horizontal bar that has a
        // nested structure where the stratification is top level, and the
        // individual data sets are nested below that.  This line gets
        // those subsets so they can be iterated over and the last layer, the
        // actual data itself, can be pulled up to the top level.
        dataSubSets = Object.keys(layer.features[0][stratification]);
        dataArray.push(
            ...handleConfidenceIntervalRowConstruction(
                layer.features[0],
                dataSubSets,
                stratification
            )
        );
    } else {
        while (layer && layer.features[index]) {
            const row = { ...layer.features[index] };
            row.id = index;

            if (stratification && currentTab.contentType === 'chart') {
                if (row[stratification]) {
                    const stratificationArr = Object.entries(
                        row[stratification][0]
                    );

                    stratificationArr.forEach((item) => {
                        const [key, value] = item;
                        row[key] = value;
                    });
                }
            }

            dataArray.push(row);
            index += 1;
        }
    }
    // Sort the array by County field or a custom field if provided
    dataArray.sort((a, b) => {
        if (a[sortField] > b[sortField]) return 1;
        if (a[sortField] <= b[sortField]) return -1;
        return 0;
    });

    return dataArray;
};

// A sortColumn carrying an explicit `order` list names a column whose values are a closed, known set
// (the Radon Levels, for example). Those get a singleSelect column so the grid's filter panel offers a
// dropdown of exactly those values instead of a free-text box or numeric operators. Returns null when
// the tab configures no sortColumn, or configures one as a plain field name with no order list.
const getSingleSelectColumn = (sortColumn) => {
    const { field, order } = parseSortColumn(sortColumn);
    if (!field || !Array.isArray(order) || !order.length) {
        return null;
    }
    return { field, order };
};

export const formatColumns = (columns, sortColumn) => {
    const singleSelect = getSingleSelectColumn(sortColumn);

    columns.forEach((column) => {
        if (singleSelect && column.field === singleSelect.field) {
            column.type = 'singleSelect';
            column.valueOptions = singleSelect.order;
            // The configured order is the intended order, so header-click sorting matches the row
            // order sortNcdmsStatewideFirst already produced rather than sorting alphabetically.
            column.sortComparator = (a, b) =>
                compareSortColumn(a, b, singleSelect.order);
        } else if (column.customFormat) {
            column.type = 'number';
            // The following `valueFormatter` function, when passed to the column definition, will override the Material
            // UI Data Grid value getter function. Data passed to the XGrid will remain the same internally, but the value
            // displayed is manipulated here -- we convert the missing(-999), less than 0.1(-555) and suppressed(-777) integer
            // values to the strings "Missing*", "<0.1", and "Suppressed*", respectively. For more info, see the Material
            // UI Data Grid documentation here: https://material-ui.com/components/data-grid/columns/#value-getter
            column.valueFormatter = ({ value }) => {
                if (value == null) {
                    // null values are present for stratification data sets - this is in place to avoid errors when using `.toLocaleString()`.
                    return 'N/A';
                }
                if (value === config.missing) {
                    return config.missingText;
                }
                if (value === config.suppressed) {
                    return config.suppressedText;
                }
                if (value === config.lessThanPointOne) {
                    return config.lessThanPointOneText;
                }
                // check if customFormat is set to 2, which indicates the value is a rate:
                if (column.customFormat === 2) {
                    return Number(value).toFixed(1); // ex: 14.3933 --> 14.4 ; 25 --> 25.0
                }

                // If none of the conditions above match (should only be used for count values):
                return value.toLocaleString(); // ex: 21679 --> 21,679
            };
        }
        // Check if we have a function definition object with a "fn" key as part of the columnHeaders
        if (column.fn) {
            createColumnFunction(column);
        }
    });
    return columns.filter((c) => !c.isNotVisible);
};

// Method to create a function within a column cell. Required if we want to create a link or other element besides
// plain text within a XDataGrid column cell. Currently renderCell is the only XDataGrid function we are using, but
// other functions can be configured here as needed in the future.
const createColumnFunction = (column) => {
    // In the db we store a JSON object with the function configuration within the fn field
    const { functionName, linkName, params, target, rel } = JSON.parse(
        column.fn
    );

    // The params array holds both the configuration for the parameters and statements for the function to be generated
    const [parameters, statements] = params;

    if (functionName === 'renderCell') {
        // We use the function constructor to create the function from a string, which is safer than eval()
        const functionCreater = {
            /* eslint-disable no-new-func */
            setHref: new Function(parameters, statements),
        };

        // Create the function and element to return on the column
        const renderCellFunction = (param) => {
            const href = functionCreater.setHref(param);
            return (
                <a href={href} target={target} rel={rel}>
                    {linkName}
                </a>
            );
        };

        // Assign the function on the column object
        column[functionName] = renderCellFunction;
    }
    return column;
};

export const formatAttributesWithCommas = (attributes) => {
    // The convention is that all numbers that need to be updated to include commas
    // should include the word 'Format' at the end of the key name:

    Object.entries(attributes).forEach((entry) => {
        if (entry[0].match(/(Format)/)) {
            attributes[entry[0]] = attributes[entry[0]].replace(
                /(\d)(?=(\d{3})+$)/g,
                '$1,'
            );
        }
    });

    return attributes;
};

// Used in the community profile age-sex breakdown chart:
export const determineChartSteps = (max) => {
    // Hard code in chart x-axis steps based on general population size.
    // This is done to eliminate chart.js attempting to change step size automatically
    // which causes chart compression issues on certain view ports.
    switch (true) {
        case max > 10000 && max <= 40000:
            return 5000;
        case max > 40000 && max <= 100000:
            return 15000;
        case max > 100000 && max <= 300000:
            return 50000;
        case max > 300000 && max <= 1000000:
            return 100000;
        case max > 1000000:
            return 1000000;
        default:
            return 0; // Means no custom steps, lets chart.js handle axis steps dynamically (only works well for smaller numbers).
    }
};

export const processCounties = (counties, topicTitle) =>
    topicTitle !== 'Birth Outcomes'
        ? counties.filter(({ county }) => county !== 'Nationwide')
        : counties;

// Method to lighten a color by changing the alpha value. If no alpha value is provided, the default is 0.6.
// Used primarily for the horizontal bar chart to lighten the color of the bars when hovered over.
// Note this assumes the color provided is a string in the format rgba(r,g,b,a), ie. 'rbga(156, 50, 56, 0.7)' and
// that a provided alpha value is a string in the format '0.7'.
export const lightenColor = (color, percentAlpha = null) => {
    const alpha = percentAlpha || '0.6';
    const newColor = color.slice(0, color.lastIndexOf(',') + 2).concat(alpha); // +2 for the index to include the comma and space
    return newColor;
};

export const splitChartTitle = (title) => {
    const middle = Math.floor(title.length / 2);

    // Find the closest space to the middle (prioritize space after middle)
    let splitIndex = title.indexOf(' ', middle);
    if (splitIndex === -1) {
        // If no space after middle, look for space before
        splitIndex = title.lastIndexOf(' ', middle);
    }

    // If still no space, return full string as one part
    if (splitIndex === -1) return [title];

    const part1 = title.slice(0, splitIndex);
    const part2 = title.slice(splitIndex + 1);

    return [part1, part2];
};

// Force DataGridPro to sort by condition which allows the table order
// to match the Confidence Interval Horizontal Bar chart order for
// the Falls > BRFSS display:
export const determineSortModel = (currentTab) =>
    currentTab.chartSettings?.chartType === 'confidenceIntervalHorizontalBar'
        ? [
              {
                  field: 'condition',
                  sort: 'asc',
              },
          ]
        : [];

export const handleMissingSuppressed = (data) =>
    data === config.missing || data === config.suppressed ? null : data;

// TODO: Temporary helper to swap "Age-adjusted" for "Age-specific" in chart/table
// titles and labels for specific TBI > Disparities displays. Preserves the casing
// of the matched text's first letter so both "Age-adjusted" and "age-adjusted" work.
const AGE_ADJUSTED_REGEX = /age-adjusted/gi;

export const applyAlternativeAgeLanguage = (
    text,
    useAlternativeTitlesAndLabels
) => {
    if (!useAlternativeTitlesAndLabels || !text) return text;

    return text.replace(AGE_ADJUSTED_REGEX, (match) => {
        const replacement = 'age-specific';
        return match[0] === match[0].toUpperCase()
            ? replacement[0].toUpperCase() + replacement.slice(1)
            : replacement;
    });
};
